import React, { useState, useEffect, useMemo } from 'react';
import { useListasConfig } from './useListasConfig';
import { db } from '../firebase';
import {
  collection, getDocs, addDoc, updateDoc, doc,
  serverTimestamp, getDoc
} from 'firebase/firestore';
import { useColeccion, useParchear, BarraActualizacion, useEstadoPersistente, useAlCambiar, usePosicionScroll, fechasATimestamp, useBorrador, useHayBorrador, useBorradorPendiente, AvisoBorrador } from './DatosContext';
import { BotonSolicitarEliminacion, AvisoSolicitudEliminacion } from './SolicitudEliminacion';
import DialogoConfirmar from './Dialogo';
import { estadoAgregadoOp, estadoAgregadoServidor, ejecutarEliminacion } from './operaciones';
import useTecladoLista, { ESTILO_ACTIVO } from './useTecladoLista';


const ESTADOS_PRESUPUESTO = [
  { valor: 'enviado', etiqueta: 'Enviado', color: '#534AB7' },
  { valor: 'aceptado', etiqueta: 'Aceptado', color: '#1D9E75' },
  { valor: 'rechazado', etiqueta: 'Rechazado', color: '#6B7280' },
];

const norm = (s) => String(s ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

// Estados de Oportunidad — colores y etiquetas (nunca verde por defecto)
const ESTADOS_OPORTUNIDAD = {
  pendiente: { etiqueta: 'Pendiente', color: '#534AB7' },
  presupuestado: { etiqueta: 'Presupuestado', color: '#BA7517' },
  aceptado: { etiqueta: 'Aceptado', color: '#1D9E75' },
  rechazado: { etiqueta: 'Rechazado', color: '#6B7280' },
  descartado: { etiqueta: 'Descartado', color: '#6B7280' },
};
const estadoOp = (valor) => ESTADOS_OPORTUNIDAD[valor] || { etiqueta: valor || '—', color: '#6B7280' };

// Estado agregado de una oportunidad a partir de los estados de TODOS sus presupuestos vinculados.
// Con un solo presupuesto reproduce exactamente la cascada de sesión 28 (caso particular N=1).
// Sesión 34: estadoAgregadoOp y estadoAgregadoServidor viven en operaciones.js (los usa también el Dashboard)

// Estados de los presupuestos vinculados a una oportunidad, derivados en memoria del listado.
// excluirId: ignora un presupuesto (el que se está moviendo/borrando). extra: añade un estado (el que se está creando/cambiando).
const estadosVinculados = (lista, oportunidadId, { excluirId = null, extra = null } = {}) => {
  const estados = oportunidadId ? (lista || []).filter(p => p.oportunidadId === oportunidadId && p.id !== excluirId).map(p => p.estado) : [];
  if (extra) estados.push(extra);
  return estados;
};

// Sesión 32 · Entrega B: estado agregado de una oportunidad calculado con los presupuestos que hay en
// Firestore EN ESTE MOMENTO (2 o 3 lecturas), no con la copia en memoria, que puede llevar hasta 30 minutos
// sin renovarse si otra persona ha tocado otro presupuesto de la misma oportunidad.
// Se llama DESPUÉS de escribir el presupuesto (ya incluye el cambio). excluirId: presupuesto que se va a borrar.

function estadoPresupuesto(valor) {
  return ESTADOS_PRESUPUESTO.find(e => e.valor === valor) || ESTADOS_PRESUPUESTO[0];
}

function EstadoBadge({ estado }) {
  const found = estadoPresupuesto(estado);
  return (
    <span style={{ background: found.color, color: '#fff', fontSize: '0.72rem', fontWeight: '500', padding: '3px 10px', borderRadius: '20px', whiteSpace: 'nowrap' }}>
      {found.etiqueta}
    </span>
  );
}

function formatearFechaCorta(fecha) {
  if (!fecha) return '—';
  const d = fecha.toDate ? fecha.toDate() : new Date(fecha);
  const meses = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];
  return `${d.getDate()} ${meses[d.getMonth()]} ${d.getFullYear()}`;
}

function formatearFechaInput(fecha) {
  if (!fecha) return '';
  const d = fecha.toDate ? fecha.toDate() : new Date(fecha);
  return d.toISOString().split('T')[0];
}

function formatearEuros(valor) {
  if (!valor && valor !== 0) return '—';
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(valor);
}
const fmtNum = (n) => n ? new Intl.NumberFormat('es-ES').format(n) : '—';

function ModalConfirmar({ titulo, mensaje, onConfirmar, onCancelar, textoCancelar, textoConfirmar }) {
  // Sesión 34: todos los avisos usan el diálogo común (la opción segura es la destacada; Enter/Esc la eligen)
  const esBorrado = /eliminar|eliminando/i.test(textoConfirmar || '');
  return (
    <DialogoConfirmar
      titulo={titulo || 'Cambios sin guardar'}
      mensaje={mensaje}
      textoSeguro={textoCancelar && textoCancelar !== 'Cancelar' ? textoCancelar : (esBorrado ? 'No eliminar' : 'Seguir editando')}
      textoPeligroso={textoConfirmar || 'Salir sin guardar'}
      onSeguro={onCancelar}
      onPeligroso={onConfirmar}
      ocupado={/^eliminando/i.test(textoConfirmar || '')}
    />
  );
}

function estiloInput(error) {
  return { background: '#0e0b24', border: `1px solid ${error ? '#D85A30' : '#534AB7'}`, borderRadius: '8px', padding: '10px 14px', color: '#ffffff', fontSize: '0.9rem', width: '100%', outline: 'none', fontFamily: 'DM Sans, sans-serif', colorScheme: 'dark' };
}

function Campo({ label, children, error, fullWidth }) {
  return (
    <div style={{ gridColumn: fullWidth ? '1 / -1' : 'span 1', display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <label style={styles.label}>{label}</label>
      {children}
      {error && <span style={{ color: '#D85A30', fontSize: '0.78rem' }}>{error}</span>}
    </div>
  );
}

function DatoFila({ label, valor }) {
  return (
    <div style={styles.datoFila}>
      <span style={styles.datoLabel}>{label}</span>
      <span style={styles.datoValor}>{valor}</span>
    </div>
  );
}

export default function Presupuestos({ esAdmin, accionInicial, onAccionCompletada, onNavegar }) {
  const [vista, setVista] = useState('listado');
  const [presupuestoSeleccionado, setPresupuestoSeleccionado] = useState(null);
  // Sesión 32 · Entrega B: presupuestos y leads vienen del almacén compartido (una descarga por sesión).
  // Los documentos se guardan crudos; el nombre y la empresa del lead se derivan aquí.
  const { items: presCrudos, setItems: setPresupuestos, cargando: cargandoPres } = useColeccion('presupuestos');
  const { items: leads, cargando: cargandoLeads } = useColeccion('leads');
  const cargando = cargandoPres || cargandoLeads;
  const presupuestos = useMemo(() => {
    const leadMap = {};
    leads.forEach(l => { leadMap[l.id] = l; });
    return presCrudos.map(p => {
      const lead = leadMap[p.leadId];
      return { ...p, leadNombre: lead ? `${lead.nombre} ${lead.apellidos || ''}`.trim() : '—', leadEmpresa: lead?.empresa || '' };
    });
  }, [presCrudos, leads]);

  // Las oportunidades no las carga este módulo: solo parchea la copia compartida si ya estaba cargada
  const parchearOps = useParchear('oportunidades');
  const alCambiarEstadoOp = (opId, estado) => parchearOps(prev => prev.map(o => o.id === opId ? { ...o, estado } : o));
  const alCrearOp = (op) => parchearOps(prev => [op, ...prev]);

  // Acción entrante desde Dashboard
  useEffect(() => {
    if (!accionInicial) return;
    if (accionInicial.accion === 'nuevo') {
      setVista('nuevo');
      if (onAccionCompletada) onAccionCompletada();
    } else if (accionInicial.accion === 'ficha' && accionInicial.id) {
      if (cargando) return; // Sesión 32: esperar a tener los datos; antes la acción se descartaba con la lista aún vacía
      const p = presupuestos.find(x => x.id === accionInicial.id);
      if (p) { setPresupuestoSeleccionado(p); setVista('ficha'); }
      if (onAccionCompletada) onAccionCompletada();
    }
  }, [accionInicial, presupuestos, cargando]);

  const abrirFicha = (pres) => { setPresupuestoSeleccionado(pres); setVista('ficha'); };

  // Sesión 34: si se salió del módulo con un borrador sin guardar en una ficha, se vuelve a ella
  const borradorPendiente = useBorradorPendiente('presupuesto');
  const borradorAbierto = React.useRef(false);
  useEffect(() => {
    if (borradorAbierto.current || !borradorPendiente || cargando || accionInicial) return;
    borradorAbierto.current = true;
    const p = presupuestos.find(x => x.id === borradorPendiente);
    if (p) { setPresupuestoSeleccionado(p); setVista('ficha'); }
  }, [borradorPendiente, cargando, presupuestos, accionInicial]);
  const volver = () => {
    setVista('listado');
    setTimeout(() => setPresupuestoSeleccionado(null), 0);
  };

  const onGuardarNuevo = async (docRef, leadSeleccionado) => {
    // getDoc para obtener timestamps reales de Firestore
    const snap = await getDoc(docRef);
    const lead = leads.find(l => l.id === leadSeleccionado.id);
    const nuevo = {
      id: snap.id,
      ...snap.data(),
      leadNombre: lead ? `${lead.nombre} ${lead.apellidos || ''}`.trim() : '—',
      leadEmpresa: lead?.empresa || '',
    };
    setPresupuestos(prev => [nuevo, ...prev]);
    volver();
  };

  const onEliminarPresupuesto = (id) => {
    setPresupuestos(prev => prev.filter(p => p.id !== id));
    volver();
  };

  if (vista === 'nuevo') return <FormularioNuevoPresupuesto leads={leads} presupuestos={presupuestos} onGuardar={onGuardarNuevo} onCancelar={volver} onEstadoOpCambiado={alCambiarEstadoOp} onOpCreada={alCrearOp} />;
  if (vista === 'ficha' && presupuestoSeleccionado) return (
    <FichaPresupuesto presupuesto={presupuestoSeleccionado} presupuestos={presupuestos} onVolver={volver}
      esAdmin={esAdmin}
      onEstadoOpCambiado={alCambiarEstadoOp}
      onActualizar={(d) => {
        const idActualizado = d.id || presupuestoSeleccionado.id;
        const dn = fechasATimestamp(d);
        setPresupuestos(prev => prev.map(p => p.id === idActualizado ? { ...p, ...dn, id: idActualizado } : p));
        setPresupuestoSeleccionado(prev => ({ ...prev, ...dn, id: idActualizado }));
      }}
      onEliminar={onEliminarPresupuesto}
      onNavegar={onNavegar} />
  );

  return (
    <div style={styles.container}>
      <div style={styles.cabecera}>
        <h1 style={styles.titulo}>Presupuestos</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <BarraActualizacion colecciones={['presupuestos', 'leads']} />
          <button style={styles.botonPrimario} onClick={() => setVista('nuevo')}>+ Nuevo presupuesto</button>
        </div>
      </div>
      <div style={styles.contenido}>
        {cargando ? <p style={{ color: '#AFA9EC', padding: '32px 0' }}>Cargando...</p> :
          <ListadoPresupuestos presupuestos={presupuestos} onAbrirFicha={abrirFicha} onPresupuestoActualizado={(id, cambios) => setPresupuestos(prev => prev.map(p => p.id === id ? { ...p, ...cambios } : p))} onEstadoOpCambiado={alCambiarEstadoOp} onNavegar={onNavegar} />}
      </div>
    </div>
  );
}

const PRESUPUESTOS_POR_PAGINA = 50;

function ListadoPresupuestos({ presupuestos, onAbrirFicha, onPresupuestoActualizado, onEstadoOpCambiado, onNavegar }) {
  const { sectores } = useListasConfig(['sectores']);
  // Sesión 32 · Entrega B: búsqueda, filtros, orden, filas visibles y scroll se recuerdan durante la sesión
  const [busqueda, setBusqueda] = useEstadoPersistente('presupuestos.busqueda', '');
  const [filtroEstado, setFiltroEstado] = useEstadoPersistente('presupuestos.filtroEstado', '');
  const [filtroSector, setFiltroSector] = useEstadoPersistente('presupuestos.filtroSector', '');
  const [filtroAnio, setFiltroAnio] = useEstadoPersistente('presupuestos.filtroAnio', '');
  const [ordenacion, setOrdenacion] = useEstadoPersistente('presupuestos.ordenacion', 'fecha_desc');
  const [visibles, setVisibles] = useEstadoPersistente('presupuestos.visibles', PRESUPUESTOS_POR_PAGINA);
  usePosicionScroll('presupuestos');

  // Reset paginación al cambiar filtros
  useAlCambiar(() => setVisibles(PRESUPUESTOS_POR_PAGINA), [busqueda, filtroEstado, filtroSector, filtroAnio, ordenacion]);

  const aniosDisponibles = [...new Set(presupuestos.map(p => { const f = p.fecha_presupuesto?.toDate ? p.fecha_presupuesto.toDate() : null; return f ? f.getFullYear() : null; }).filter(Boolean))].sort((a, b) => b - a);

  const filtrados = presupuestos.filter(p => {
    const texto = norm(busqueda);
    return (!busqueda || norm(p.num_presupuesto).includes(texto) || norm(p.leadNombre).includes(texto) || norm(p.leadEmpresa).includes(texto) || norm(p.sector).includes(texto)) &&
      (!filtroEstado || p.estado === filtroEstado) &&
      (!filtroSector || p.sector === filtroSector) &&
      (!filtroAnio || (p.fecha_presupuesto?.toDate && p.fecha_presupuesto.toDate().getFullYear() === parseInt(filtroAnio)));
  }).sort((a, b) => {
    const tsA = a.fecha_presupuesto?.seconds ? a.fecha_presupuesto.seconds : (a.fecha_presupuesto instanceof Date ? a.fecha_presupuesto.getTime() / 1000 : 0);
    const tsB = b.fecha_presupuesto?.seconds ? b.fecha_presupuesto.seconds : (b.fecha_presupuesto instanceof Date ? b.fecha_presupuesto.getTime() / 1000 : 0);
    if (ordenacion === 'fecha_desc') return tsB - tsA;
    if (ordenacion === 'fecha_asc') return tsA - tsB;
    if (ordenacion === 'importe_desc') return (b.importe || 0) - (a.importe || 0);
    if (ordenacion === 'importe_asc') return (a.importe || 0) - (b.importe || 0);
    return 0;
  });

  // KPIs: solo filtran por año y sector — nunca por estado ni búsqueda
  // (misma lógica que Oportunidades: tasa y volumen miden el periodo/sector completo)
  const baseKpi = presupuestos.filter(p => {
    const coincideSector = !filtroSector || p.sector === filtroSector;
    const coincideAnio = !filtroAnio || (p.fecha_presupuesto?.toDate && p.fecha_presupuesto.toDate().getFullYear() === parseInt(filtroAnio));
    return coincideSector && coincideAnio;
  });
  const total = presupuestos.length;
  const enviados = baseKpi.filter(p => p.estado === 'enviado').length;
  const aceptados = baseKpi.filter(p => p.estado === 'aceptado').length;
  const rechazados = baseKpi.filter(p => p.estado === 'rechazado').length;
  const conRespuesta = aceptados + rechazados;
  const tasaAceptacion = conRespuesta > 0 ? (aceptados / conRespuesta) * 100 : 0;
  const volumenAceptado = baseKpi.filter(p => p.estado === 'aceptado').reduce((s, p) => s + (p.importe_encargo || p.importe || 0), 0);
  const hayFiltros = busqueda || filtroEstado || filtroSector || filtroAnio;

  const aceptarPresupuesto = async (e, pres) => {
    e.stopPropagation();
    try {
      await updateDoc(doc(db, 'presupuestos', pres.id), { estado: 'aceptado' });
      if (pres.oportunidadId && pres.leadId) {
        const estadoOpNuevo = await estadoAgregadoServidor(pres.oportunidadId);
        await updateDoc(doc(db, 'leads', pres.leadId, 'posibles_proyectos', pres.oportunidadId), { estado: estadoOpNuevo });
        onEstadoOpCambiado(pres.oportunidadId, estadoOpNuevo);
      }
      onPresupuestoActualizado(pres.id, { estado: 'aceptado' });
    } catch (err) { console.error(err); }
  };

  const rechazarPresupuesto = async (e, pres) => {
    e.stopPropagation();
    try {
      await updateDoc(doc(db, 'presupuestos', pres.id), { estado: 'rechazado' });
      if (pres.oportunidadId && pres.leadId) {
        const estadoOpNuevo = await estadoAgregadoServidor(pres.oportunidadId);
        await updateDoc(doc(db, 'leads', pres.leadId, 'posibles_proyectos', pres.oportunidadId), { estado: estadoOpNuevo });
        onEstadoOpCambiado(pres.oportunidadId, estadoOpNuevo);
      }
      onPresupuestoActualizado(pres.id, { estado: 'rechazado' });
    } catch (err) { console.error(err); }
  };

  return (
    <>
      <div style={styles.gridKpis}>
        <div style={styles.kpiCard}><p style={styles.kpiLabel}>Total{hayFiltros ? ' (filtrado)' : ''}</p><p style={styles.kpiValor}>{total}</p></div>
        <div style={styles.kpiCard}><p style={styles.kpiLabel}>Pendientes respuesta</p><p style={{ ...styles.kpiValor, color: '#534AB7' }}>{enviados}</p></div>
        <div style={styles.kpiCard}><p style={styles.kpiLabel}>Aceptados</p><p style={{ ...styles.kpiValor, color: '#1D9E75' }}>{aceptados}</p></div>
        <div style={styles.kpiCard}><p style={styles.kpiLabel}>Rechazados</p><p style={{ ...styles.kpiValor, color: '#6B7280' }}>{rechazados}</p></div>
        <div style={styles.kpiCard}>
          <p style={styles.kpiLabel}>Tasa de aceptación</p>
          <p style={{ ...styles.kpiValor, color: tasaAceptacion >= 50 ? '#1D9E75' : tasaAceptacion >= 30 ? '#BA7517' : '#D85A30' }}>
            {conRespuesta > 0 ? `${new Intl.NumberFormat('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(tasaAceptacion)}%` : '—'}{conRespuesta > 0 && <span style={{ fontSize: '0.85rem', color: '#AFA9EC', marginLeft: '8px' }}>({aceptados} de {conRespuesta})</span>}
          </p>
        </div>
        <div style={styles.kpiCard}><p style={styles.kpiLabel}>Volumen aceptado</p><p style={{ ...styles.kpiValor, color: '#1D9E75', fontSize: '1.3rem' }}>{formatearEuros(volumenAceptado)}</p></div>
      </div>

      <div style={styles.filtrosBar}>
        <input style={styles.buscador} placeholder="Buscar por número, lead, sector..." value={busqueda} onChange={e => setBusqueda(e.target.value)} />
        <div style={styles.pills}>
          {ESTADOS_PRESUPUESTO.map(e => (
            <button key={e.valor} style={{ ...styles.pill, background: filtroEstado === e.valor ? e.color : 'transparent', color: filtroEstado === e.valor ? '#fff' : '#AFA9EC', borderColor: filtroEstado === e.valor ? e.color : 'rgba(83,74,183,0.3)' }} onClick={() => setFiltroEstado(filtroEstado === e.valor ? '' : e.valor)}>{e.etiqueta}</button>
          ))}
        </div>
        <select style={styles.select} value={filtroSector} onChange={e => setFiltroSector(e.target.value)}><option value="">Todos los sectores</option>{sectores.map(s => <option key={s} value={s}>{s}</option>)}</select>
        <select style={styles.select} value={filtroAnio} onChange={e => setFiltroAnio(e.target.value)}><option value="">Todos los años</option>{aniosDisponibles.map(a => <option key={a} value={a}>{a}</option>)}</select>
        <select style={styles.select} value={ordenacion} onChange={e => setOrdenacion(e.target.value)}>
          <option value="fecha_desc">Más recientes</option><option value="fecha_asc">Más antiguos</option>
          <option value="importe_desc">Mayor importe</option><option value="importe_asc">Menor importe</option>
        </select>
        {hayFiltros && <button style={styles.botonLimpiar} onClick={() => { setBusqueda(''); setFiltroEstado(''); setFiltroSector(''); setFiltroAnio(''); }}>Limpiar filtros</button>}
      </div>

      {filtrados.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#AFA9EC' }}>{hayFiltros ? 'No hay presupuestos que coincidan con los filtros.' : 'Aún no hay presupuestos registrados.'}</div>
      ) : (
        <div style={styles.tablaWrapper}>
          <table style={styles.tabla}>
            <thead><tr>{['Nº Presupuesto','Lead / Oportunidad','Sector','Importe','€/m²','Estado','Fecha',''].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
            <tbody>
              {filtrados.slice(0, visibles).map(pres => (
                <tr key={pres.id} style={styles.tr} onClick={() => onAbrirFicha(pres)}>
                  <td style={styles.td}><span style={styles.nombreLead}>{pres.num_presupuesto || '—'}</span></td>
                  <td style={styles.td}><span style={styles.nombreLead}>{pres.leadNombre}</span>{pres.leadEmpresa && <span style={styles.subLead}>{pres.leadEmpresa}</span>}{pres.oportunidad_ciudad && <span style={styles.subLead}>{pres.oportunidad_ciudad}</span>}</td>
                  <td style={styles.td}>{pres.sector || '—'}</td>
                  <td style={styles.td}>{formatearEuros(pres.importe)}</td>
                  <td style={styles.td}>{pres.importe_m2 ? `${fmtNum(Math.round(pres.importe_m2))} €/m²` : '—'}</td>
                  <td style={styles.td} onClick={e => e.stopPropagation()}><EstadoBadge estado={pres.estado} /></td>
                  <td style={styles.td}>{formatearFechaCorta(pres.fecha_presupuesto)}</td>
                  <td style={styles.td} onClick={e => e.stopPropagation()}>
                    {pres.estado === 'enviado' && <div style={{ display: 'flex', gap: '6px' }}><button style={styles.botonAceptar} onClick={e => aceptarPresupuesto(e, pres)}>Aceptar ✓</button><button style={styles.botonCerrar} onClick={e => rechazarPresupuesto(e, pres)}>Rechazar</button></div>}
                    {pres.estado === 'aceptado' && (
                      pres.proyectoId
                        ? <button style={{ ...styles.botonProyecto, background: 'transparent', border: '1px solid #1D9E75', color: '#1D9E75' }} onClick={e => { e.stopPropagation(); onNavegar && onNavegar({ modulo: 'proyectos', accion: 'ficha', id: pres.proyectoId }); }}>Ver proyecto →</button>
                        : <button style={styles.botonProyecto} onClick={e => { e.stopPropagation(); onNavegar && onNavegar({ modulo: 'proyectos', accion: 'nuevo', presupuestoId: pres.id, origen: 'presupuestos' }); }}>→ Crear proyecto</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {filtrados.length > visibles && (
        <div style={{ textAlign: 'center', padding: '24px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
          <button style={{ ...styles.botonSecundario, padding: '10px 32px' }} onClick={() => setVisibles(v => v + PRESUPUESTOS_POR_PAGINA)}>
            Mostrar 50 más
          </button>
          <span style={{ color: '#AFA9EC', fontSize: '0.78rem' }}>
            Mostrando {Math.min(visibles, filtrados.length)} de {filtrados.length} — usa el buscador para filtrar
          </span>
        </div>
      )}
    </>
  );
}

function FormularioNuevoPresupuesto({ leads, presupuestos, onGuardar, onCancelar, onEstadoOpCambiado, onOpCreada }) {
  const { sectores, tiposProyecto } = useListasConfig(['sectores', 'tipos_proyecto']);
  const [busquedaLead, setBusquedaLead] = useState('');
  const [leadSeleccionado, setLeadSeleccionado] = useState(null);
  const [oportunidades, setOportunidades] = useState([]);
  const [oportunidadSeleccionada, setOportunidadSeleccionada] = useState(null);
  const [form, setForm] = useState({ num_presupuesto: '', fecha_presupuesto: new Date().toISOString().split('T')[0], sector: '', tipo_encargo: '', importe: '', superficie: '', importe_m2: '', estado: 'enviado', num_encargo: '', fecha_encargo: '', importe_encargo: '', descripcion: '' });
  const [guardando, setGuardando] = useState(false);
  const [errores, setErrores] = useState({});
  const FORM_INICIAL = { num_presupuesto: '', fecha_presupuesto: new Date().toISOString().split('T')[0], sector: '', tipo_encargo: '', importe: '', superficie: '', importe_m2: '', estado: 'enviado', num_encargo: '', fecha_encargo: '', importe_encargo: '', descripcion: '' };
  const [modal, setModal] = useState(false);
  // Estado para nueva oportunidad inline
  const [mostrarFormOp, setMostrarFormOp] = useState(false);
  const [formOp, setFormOp] = useState({ sector: '', ciudad: '', provincia: '', superficie: '', fecha_entrada: new Date().toISOString().split('T')[0] });
  const [guardandoOp, setGuardandoOp] = useState(false);

  const hayDatos = leadSeleccionado || oportunidadSeleccionada || JSON.stringify(form) !== JSON.stringify(FORM_INICIAL);

  const set = (campo, valor) => {
    setForm(f => {
      const nuevo = { ...f, [campo]: valor };
      if (campo === 'importe' || campo === 'superficie') {
        const imp = parseFloat(campo === 'importe' ? valor : f.importe) || 0;
        const sup = parseFloat(campo === 'superficie' ? valor : f.superficie) || 0;
        nuevo.importe_m2 = imp > 0 && sup > 0 ? (imp / sup).toFixed(2) : '';
      }
      return nuevo;
    });
  };

  const leadsFiltrados = leads.filter(l => {
    const t = norm(busquedaLead);
    return !t || norm(`${l.nombre || ''} ${l.apellidos || ''}`).includes(t) || norm(l.empresa).includes(t);
  }).slice(0, 8);

  const seleccionarLead = async (lead) => {
    setLeadSeleccionado(lead); setBusquedaLead(''); setOportunidadSeleccionada(null); setMostrarFormOp(false);
    try { const snap = await getDocs(collection(db, 'leads', lead.id, 'posibles_proyectos')); setOportunidades(snap.docs.map(d => ({ id: d.id, ...d.data() }))); } catch (e) { console.error(e); }
  };

  const teclaLead = useTecladoLista(busquedaLead ? leadsFiltrados : [], seleccionarLead, () => setBusquedaLead(''));
  const seleccionarOportunidad = (op) => { setOportunidadSeleccionada(op); setForm(f => ({ ...f, sector: op.sector || f.sector, superficie: op.superficie ? String(op.superficie) : f.superficie })); };

  // Crear oportunidad inline sin salir del formulario
  const crearOportunidadInline = async () => {
    if (!formOp.fecha_entrada) return;
    setGuardandoOp(true);
    try {
      const docRef = await addDoc(collection(db, 'leads', leadSeleccionado.id, 'posibles_proyectos'), {
        estado: 'pendiente',
        sector: formOp.sector || '',
        ciudad: formOp.ciudad || '',
        provincia: formOp.provincia || '',
        superficie: parseFloat(formOp.superficie) || null,
        fecha_entrada: new Date(formOp.fecha_entrada),
        creado_en: serverTimestamp(),
      });
      const snap = await getDoc(docRef);
      const nuevaOp = { id: snap.id, ...snap.data() };
      if (onOpCreada) onOpCreada({ ...nuevaOp, leadId: leadSeleccionado.id });
      setOportunidades(prev => [nuevaOp, ...prev]);
      seleccionarOportunidad(nuevaOp);
      setMostrarFormOp(false);
      setFormOp({ sector: '', ciudad: '', provincia: '', superficie: '', fecha_entrada: new Date().toISOString().split('T')[0] });
    } catch (e) { console.error(e); }
    setGuardandoOp(false);
  };

  const validar = () => {
    const e = {};
    if (!leadSeleccionado) e.lead = 'Debes seleccionar un lead';
    if (!oportunidadSeleccionada) e.oportunidad = 'Debes seleccionar una oportunidad';
    if (!form.fecha_presupuesto) e.fecha = 'La fecha es obligatoria';
    if (!form.num_presupuesto.trim()) e.num_presupuesto = 'El número de presupuesto es obligatorio';
    if (!form.importe) e.importe = 'El importe es obligatorio';
    setErrores(e); return Object.keys(e).length === 0;
  };

  const guardar = async () => {
    if (!validar()) return; setGuardando(true);
    try {
      const docRef = await addDoc(collection(db, 'presupuestos'), {
        num_presupuesto: form.num_presupuesto,
        fecha_presupuesto: new Date(form.fecha_presupuesto),
        leadId: leadSeleccionado.id,
        oportunidadId: oportunidadSeleccionada.id,
        oportunidad_ciudad: oportunidadSeleccionada.ciudad || '',
        sector: form.sector,
        tipo_encargo: form.tipo_encargo,
        importe: parseFloat(form.importe) || 0,
        superficie: parseFloat(form.superficie) || null,
        importe_m2: parseFloat(form.importe_m2) || null,
        descripcion: form.descripcion,
        estado: form.estado,
        num_encargo: form.estado === 'aceptado' ? form.num_encargo : '',
        fecha_encargo: form.estado === 'aceptado' && form.fecha_encargo ? new Date(form.fecha_encargo) : null,
        importe_encargo: form.estado === 'aceptado' && form.importe_encargo ? parseFloat(form.importe_encargo) : null,
        creado_en: serverTimestamp(),
      });
      const estadoOpNuevo = await estadoAgregadoServidor(oportunidadSeleccionada.id);
      await updateDoc(doc(db, 'leads', leadSeleccionado.id, 'posibles_proyectos', oportunidadSeleccionada.id), { estado: estadoOpNuevo });
      if (onEstadoOpCambiado) onEstadoOpCambiado(oportunidadSeleccionada.id, estadoOpNuevo);
      // getDoc para obtener timestamps reales — REGLA PERMANENTE
      onGuardar(docRef, leadSeleccionado);
    } catch (e) { console.error(e); setGuardando(false); }
  };

  const intentarCancelar = () => { if (hayDatos) { setModal(true); } else { onCancelar(); } };

  return (
    <div style={{ ...styles.container, paddingBottom: '100px' }}>
      {modal && <ModalConfirmar titulo="Cambios sin guardar" mensaje="¿Quieres salir sin guardar este presupuesto?" textoCancelar="Seguir editando" textoConfirmar="Salir sin guardar" onConfirmar={onCancelar} onCancelar={() => setModal(false)} />}
      <div style={styles.cabecera}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button style={styles.botonVolver} onClick={intentarCancelar}>← Presupuestos</button>
          <h1 style={styles.titulo}>Nuevo presupuesto</h1>
        </div>
      </div>
      <div style={styles.formGrid}>
        <div style={styles.seccion}>
          <h2 style={styles.seccionTitulo}>Lead asociado *</h2>
          {leadSeleccionado ? (
            <div style={styles.seleccionado}>
              <div><span style={styles.nombreLead}>{leadSeleccionado.nombre} {leadSeleccionado.apellidos || ''}</span>{leadSeleccionado.empresa && <span style={{ color: '#7B6FE8', fontSize: '0.82rem', marginLeft: '8px' }}>{leadSeleccionado.empresa}</span>}</div>
              <button style={styles.botonEditar} onClick={() => { setLeadSeleccionado(null); setOportunidades([]); setOportunidadSeleccionada(null); setMostrarFormOp(false); }}>Cambiar</button>
            </div>
          ) : (
            <div>
              <input style={styles.buscador} placeholder="Buscar lead por nombre o empresa..." value={busquedaLead} onChange={e => setBusquedaLead(e.target.value)} onKeyDown={teclaLead.onKeyDown} />
              {busquedaLead && <div style={styles.desplegable}>{leadsFiltrados.length === 0 ? <p style={{ color: '#AFA9EC', padding: '12px 16px', fontSize: '0.85rem' }}>No se encontraron leads</p> : leadsFiltrados.map((l, i) => <div key={l.id} style={{ ...styles.opcionDesplegable, ...(i === teclaLead.activo ? ESTILO_ACTIVO : {}) }} onMouseEnter={() => teclaLead.setActivo(i)} onClick={() => seleccionarLead(l)}><span style={{ color: '#ffffff', fontSize: '0.88rem' }}>{l.nombre} {l.apellidos || ''}</span>{l.empresa && <span style={{ color: '#AFA9EC', fontSize: '0.78rem', marginLeft: '8px' }}>{l.empresa}</span>}</div>)}</div>}
              {errores.lead && <p style={styles.error}>{errores.lead}</p>}
            </div>
          )}
        </div>

        {leadSeleccionado && (
          <div style={styles.seccion}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <h2 style={{ ...styles.seccionTitulo, marginBottom: 0 }}>Oportunidad asociada *</h2>
              {!mostrarFormOp && (
                <button style={{ ...styles.botonSecundario, fontSize: '0.82rem', padding: '6px 14px' }} onClick={() => setMostrarFormOp(true)}>
                  + Nueva oportunidad
                </button>
              )}
            </div>

            {/* Formulario inline de nueva oportunidad */}
            {mostrarFormOp && (
              <div style={{ background: '#0e0b24', borderRadius: '10px', padding: '16px', border: '1px solid rgba(83,74,183,0.4)', marginBottom: '16px' }}>
                <p style={{ color: '#7B6FE8', fontSize: '0.82rem', fontWeight: '500', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Nueva oportunidad para este lead</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <Campo label="Fecha *"><input type="date" className="metron-date" style={estiloInput()} value={formOp.fecha_entrada} onChange={e => setFormOp(f => ({ ...f, fecha_entrada: e.target.value }))} /></Campo>
                  <Campo label="Sector"><select style={{ ...estiloInput(), background: '#1a1440' }} value={formOp.sector} onChange={e => setFormOp(f => ({ ...f, sector: e.target.value }))}><option value="">Sin sector</option>{sectores.map(s => <option key={s} value={s}>{s}</option>)}</select></Campo>
                  <Campo label="Ciudad"><input style={estiloInput()} value={formOp.ciudad} onChange={e => setFormOp(f => ({ ...f, ciudad: e.target.value }))} placeholder="A Coruña" /></Campo>
                  <Campo label="Provincia"><input style={estiloInput()} value={formOp.provincia} onChange={e => setFormOp(f => ({ ...f, provincia: e.target.value }))} placeholder="A Coruña" /></Campo>
                  <Campo label="Superficie (m²)"><input type="number" style={{ ...estiloInput(), colorScheme: 'dark' }} value={formOp.superficie} onChange={e => setFormOp(f => ({ ...f, superficie: e.target.value }))} placeholder="0" /></Campo>
                </div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '12px', justifyContent: 'flex-end' }}>
                  <button style={{ ...styles.botonSecundario, fontSize: '0.82rem', padding: '7px 14px' }} onClick={() => setMostrarFormOp(false)}>Cancelar</button>
                  <button style={{ ...styles.botonPrimario, fontSize: '0.82rem', padding: '7px 14px' }} onClick={crearOportunidadInline} disabled={guardandoOp}>{guardandoOp ? 'Creando...' : 'Crear y seleccionar'}</button>
                </div>
              </div>
            )}

            {oportunidades.length === 0 && !mostrarFormOp ? (
              <div style={{ background: 'rgba(186,117,23,0.08)', border: '1px solid rgba(186,117,23,0.3)', borderRadius: '8px', padding: '14px 16px' }}>
                <p style={{ color: '#BA7517', fontSize: '0.85rem', marginBottom: '4px' }}>Este lead no tiene oportunidades registradas.</p>
                <p style={{ color: '#AFA9EC', fontSize: '0.78rem' }}>Pulsa "+ Nueva oportunidad" para crear una sin salir de aquí.</p>
              </div>
            ) : oportunidades.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {oportunidades.map(op => {
                  const vinculados = presupuestos.filter(p => p.oportunidadId === op.id);
                  return (
                    <div key={op.id} style={{ ...styles.opcionOportunidad, borderColor: oportunidadSeleccionada?.id === op.id ? '#1D9E75' : 'rgba(83,74,183,0.2)', background: oportunidadSeleccionada?.id === op.id ? 'rgba(29,158,117,0.1)' : '#0e0b24' }} onClick={() => seleccionarOportunidad(op)}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px', flexWrap: 'wrap' }}>
                        <span style={{ background: estadoOp(op.estado).color, color: '#fff', fontSize: '0.68rem', padding: '2px 8px', borderRadius: '20px' }}>{estadoOp(op.estado).etiqueta}</span>
                        {op.sector && <span style={{ color: '#AFA9EC', fontSize: '0.78rem' }}>{op.sector}</span>}
                        <span style={{ color: '#AFA9EC', fontSize: '0.78rem' }}>{formatearFechaCorta(op.fecha_entrada)}</span>
                      </div>
                      <p style={{ color: '#ffffff', fontSize: '0.85rem' }}>{[op.ciudad, op.provincia, op.pais].filter(Boolean).join(', ') || 'Sin ubicación'}{op.superficie && <span style={{ color: '#AFA9EC', marginLeft: '8px' }}>{op.superficie} m²</span>}</p>
                      {vinculados.length > 0 && (
                        <p style={{ color: '#AFA9EC', fontSize: '0.75rem', marginTop: '6px' }}>Vinculada a {vinculados.length} presupuesto{vinculados.length > 1 ? 's' : ''}: {vinculados.map(p => `nº ${p.num_presupuesto || '—'}`).join(', ')}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : null}
            {errores.oportunidad && <p style={styles.error}>{errores.oportunidad}</p>}
          </div>
        )}

        <div style={styles.seccion}>
          <h2 style={styles.seccionTitulo}>Datos del presupuesto</h2>
          <div style={styles.camposGrid}>
            <Campo label="Nº Presupuesto *" error={errores.num_presupuesto}><input style={estiloInput(errores.num_presupuesto)} value={form.num_presupuesto} onChange={e => set('num_presupuesto', e.target.value)} placeholder={(() => { const h = new Date(); const yy = String(h.getFullYear()).slice(-2); const mm = String(h.getMonth()+1).padStart(2,'0'); const dd = String(h.getDate()).padStart(2,'0'); return `${yy}IE${yy}${mm}${dd}-XXX`; })()} /></Campo>
            <Campo label="Fecha *" error={errores.fecha}><input type="date" className="metron-date" style={estiloInput(errores.fecha)} value={form.fecha_presupuesto} onChange={e => set('fecha_presupuesto', e.target.value)} /></Campo>
            <Campo label="Sector"><select style={estiloInput()} value={form.sector} onChange={e => set('sector', e.target.value)}><option value="">Seleccionar sector</option>{sectores.map(s => <option key={s} value={s}>{s}</option>)}</select></Campo>
            <Campo label="Tipo de encargo"><select style={estiloInput()} value={form.tipo_encargo} onChange={e => set('tipo_encargo', e.target.value)}><option value="">Seleccionar tipo</option>{tiposProyecto.map(t => <option key={t} value={t}>{t}</option>)}</select></Campo>
            <Campo label="Importe (€) *" error={errores.importe}><input type="number" style={estiloInput(errores.importe)} value={form.importe} onChange={e => set('importe', e.target.value)} placeholder="0" /></Campo>
            <Campo label="Superficie (m²)"><input type="number" style={estiloInput()} value={form.superficie} onChange={e => set('superficie', e.target.value)} placeholder="0" /></Campo>
            <Campo label="€/m² (calculado automáticamente)"><input style={{ ...estiloInput(), color: '#AFA9EC', cursor: 'not-allowed' }} value={form.importe_m2 ? `${Math.round(form.importe_m2)} €/m²` : ''} readOnly placeholder="—" /></Campo>
            <Campo label="Descripción" fullWidth><textarea style={{ ...estiloInput(), resize: 'vertical', minHeight: '80px' }} value={form.descripcion} onChange={e => set('descripcion', e.target.value)} placeholder="Descripción del presupuesto..." /></Campo>
          </div>
        </div>

        <div style={styles.seccion}>
          <h2 style={styles.seccionTitulo}>Estado</h2>
          <div style={{ display: 'flex', gap: '10px', marginBottom: form.estado === 'aceptado' ? '24px' : '0' }}>
            {ESTADOS_PRESUPUESTO.map(e => <button key={e.valor} style={{ ...styles.pill, background: form.estado === e.valor ? e.color : 'transparent', color: form.estado === e.valor ? '#fff' : '#AFA9EC', borderColor: form.estado === e.valor ? e.color : 'rgba(83,74,183,0.3)', padding: '8px 20px', fontSize: '0.88rem' }} onClick={() => set('estado', e.valor)}>{e.etiqueta}</button>)}
          </div>
          {form.estado === 'aceptado' && (
            <div style={styles.panelEncargo}>
              <p style={{ color: '#1D9E75', fontSize: '0.82rem', fontWeight: '500', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Datos del encargo</p>
              <div style={styles.camposGrid}>
                <Campo label="Nº Encargo"><input style={estiloInput()} value={form.num_encargo} onChange={e => set('num_encargo', e.target.value)} placeholder="Formato libre" /></Campo>
                <Campo label="Fecha de encargo"><input type="date" className="metron-date" style={estiloInput()} value={form.fecha_encargo} onChange={e => set('fecha_encargo', e.target.value)} /></Campo>
                <Campo label="Importe encargo (€) si difiere"><input type="number" style={estiloInput()} value={form.importe_encargo} onChange={e => set('importe_encargo', e.target.value)} placeholder={form.importe || '0'} /></Campo>
              </div>
            </div>
          )}
        </div>
      </div>
      <div style={styles.barraGuardar}>
        <button style={styles.botonSecundario} onClick={intentarCancelar}>Cancelar</button>
        <button style={styles.botonPrimario} onClick={guardar} disabled={guardando}>{guardando ? 'Guardando...' : 'Guardar presupuesto'}</button>
      </div>
    </div>
  );
}

function FichaPresupuesto({ presupuesto, presupuestos, onVolver, onActualizar, onEliminar, onNavegar, onEstadoOpCambiado, esAdmin }) {
  const { sectores, tiposProyecto } = useListasConfig(['sectores', 'tipos_proyecto']);
  // Sesión 34: borrador — si quedaron cambios sin guardar de esta ficha, se reabre en edición con ellos
  const claveBorrador = `presupuesto:${presupuesto.id}`;
  const hayBorrador = useHayBorrador(claveBorrador);
  const [modoEdicion, setModoEdicion] = useState(hayBorrador);
  const [avisoBorrador, setAvisoBorrador] = useState(hayBorrador);
  const [datosLocales, setDatosLocales] = useState(presupuesto);
  const [form, setForm, , reiniciarForm] = useBorrador(claveBorrador, { ...presupuesto, fecha_presupuesto: formatearFechaInput(presupuesto.fecha_presupuesto), fecha_encargo: formatearFechaInput(presupuesto.fecha_encargo), importe: presupuesto.importe || '', superficie: presupuesto.superficie || '', importe_m2: presupuesto.importe_m2 || '', importe_encargo: presupuesto.importe_encargo || '' });
  const [guardando, setGuardando] = useState(false);
  const [modal, setModal] = useState(false);
  const [modalEliminar, setModalEliminar] = useState(false);
  const [eliminando, setEliminando] = useState(false);

  // Oportunidad vinculada — verificación de existencia real (regla sesión 29)
  const [opVinculada, setOpVinculada] = useState(null);
  const [cargandoVinculo, setCargandoVinculo] = useState(true);
  const [modalOp, setModalOp] = useState(false);
  const [opsLead, setOpsLead] = useState([]);
  const [cargandoOps, setCargandoOps] = useState(false);
  const [opElegida, setOpElegida] = useState(null);
  const [vinculando, setVinculando] = useState(false);

  useEffect(() => {
    let activo = true;
    const cargarVinculo = async () => {
      if (!datosLocales.leadId || !datosLocales.oportunidadId) {
        if (activo) { setOpVinculada(null); setCargandoVinculo(false); }
        return;
      }
      setCargandoVinculo(true);
      try {
        const snap = await getDoc(doc(db, 'leads', datosLocales.leadId, 'posibles_proyectos', datosLocales.oportunidadId));
        if (activo) setOpVinculada(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      } catch (e) { console.error(e); if (activo) setOpVinculada(null); }
      if (activo) setCargandoVinculo(false);
    };
    cargarVinculo();
    return () => { activo = false; };
  }, [datosLocales.leadId, datosLocales.oportunidadId]);

  // 'cargando' | 'sin' (nunca vinculado) | 'rota' (referencia a documento inexistente) | 'ok'
  const estadoVinculo = cargandoVinculo ? 'cargando' : !datosLocales.oportunidadId ? 'sin' : opVinculada ? 'ok' : 'rota';

  const abrirSelectorOp = async () => {
    setModalOp(true); setOpElegida(null); setCargandoOps(true);
    try {
      if (datosLocales.leadId) {
        const snap = await getDocs(collection(db, 'leads', datosLocales.leadId, 'posibles_proyectos'));
        setOpsLead(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } else setOpsLead([]);
    } catch (e) { console.error(e); setOpsLead([]); }
    setCargandoOps(false);
  };

  // Estado que tendrá cada oportunidad tras vincular (agregado sobre todos sus presupuestos)
  const estadoDestino = opElegida ? estadoAgregadoOp(estadosVinculados(presupuestos, opElegida.id, { excluirId: presupuesto.id, extra: datosLocales.estado })) : null;
  const esRelink = !!(opElegida && estadoVinculo === 'ok' && opVinculada && opVinculada.id !== opElegida.id);
  const estadoAnteriorTras = esRelink ? estadoAgregadoOp(estadosVinculados(presupuestos, datosLocales.oportunidadId, { excluirId: presupuesto.id })) : null;
  // Otros presupuestos vinculados a la misma oportunidad que este
  const otrosPresupuestosOp = datosLocales.oportunidadId ? presupuestos.filter(p => p.oportunidadId === datosLocales.oportunidadId && p.id !== presupuesto.id) : [];

  const vincularOportunidad = async () => {
    if (!opElegida || vinculando) return;
    setVinculando(true);
    try {
      const anteriorId = datosLocales.oportunidadId;
      // 1. Presupuesto: el vínculo vive SOLO aquí (fuente única de verdad) + ciudad desnormalizada
      const cambios = { oportunidadId: opElegida.id, oportunidad_ciudad: opElegida.ciudad || '' };
      await updateDoc(doc(db, 'presupuestos', presupuesto.id), cambios);
      // 2. Oportunidad nueva: estado agregado leído de Firestore ahora (ya incluye este presupuesto)
      const estadoNuevaOp = await estadoAgregadoServidor(opElegida.id);
      await updateDoc(doc(db, 'leads', datosLocales.leadId, 'posibles_proyectos', opElegida.id), { estado: estadoNuevaOp });
      if (onEstadoOpCambiado) onEstadoOpCambiado(opElegida.id, estadoNuevaOp);
      // 3. Oportunidad anterior (re-vinculación): estado agregado de los presupuestos que le quedan (pendiente si ninguno)
      if (anteriorId && anteriorId !== opElegida.id && opVinculada) {
        const estadoAnterior = await estadoAgregadoServidor(anteriorId);
        await updateDoc(doc(db, 'leads', datosLocales.leadId, 'posibles_proyectos', anteriorId), { estado: estadoAnterior });
        if (onEstadoOpCambiado) onEstadoOpCambiado(anteriorId, estadoAnterior);
      }
      setDatosLocales(prev => ({ ...prev, ...cambios }));
      setOpVinculada({ ...opElegida, estado: estadoNuevaOp });
      onActualizar({ ...cambios, id: presupuesto.id });
      setModalOp(false); setOpElegida(null);
    } catch (e) { console.error(e); }
    setVinculando(false);
  };

  const set = (campo, valor) => {
    setForm(f => {
      const nuevo = { ...f, [campo]: valor };
      if (campo === 'importe' || campo === 'superficie') { const imp = parseFloat(campo === 'importe' ? valor : f.importe) || 0; const sup = parseFloat(campo === 'superficie' ? valor : f.superficie) || 0; nuevo.importe_m2 = imp > 0 && sup > 0 ? (imp / sup).toFixed(2) : ''; }
      return nuevo;
    });
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      const dg = { num_presupuesto: form.num_presupuesto, fecha_presupuesto: new Date(form.fecha_presupuesto), sector: form.sector, tipo_encargo: form.tipo_encargo, importe: parseFloat(form.importe) || 0, superficie: parseFloat(form.superficie) || null, importe_m2: parseFloat(form.importe_m2) || null, descripcion: form.descripcion || '', estado: form.estado, num_encargo: form.num_encargo || '', fecha_encargo: form.fecha_encargo ? new Date(form.fecha_encargo) : null, importe_encargo: form.importe_encargo ? parseFloat(form.importe_encargo) : null };
      await updateDoc(doc(db, 'presupuestos', presupuesto.id), dg);
      if (form.estado !== datosLocales.estado && datosLocales.leadId && datosLocales.oportunidadId) {
        const nuevoEstadoOp = await estadoAgregadoServidor(datosLocales.oportunidadId);
        await updateDoc(doc(db, 'leads', datosLocales.leadId, 'posibles_proyectos', datosLocales.oportunidadId), { estado: nuevoEstadoOp });
        if (onEstadoOpCambiado) onEstadoOpCambiado(datosLocales.oportunidadId, nuevoEstadoOp);
        setOpVinculada(prev => prev ? { ...prev, estado: nuevoEstadoOp } : prev);
      }
      setDatosLocales({ ...datosLocales, ...dg, fecha_presupuesto: { toDate: () => new Date(form.fecha_presupuesto) } });
      reiniciarForm(form); setAvisoBorrador(false);
      setModoEdicion(false); onActualizar({ ...dg, id: presupuesto.id });
    } catch (e) { console.error(e); }
    setGuardando(false);
  };

  const confirmarSalida = () => { setAvisoBorrador(false); reiniciarForm({ ...datosLocales, fecha_presupuesto: formatearFechaInput(datosLocales.fecha_presupuesto), fecha_encargo: formatearFechaInput(datosLocales.fecha_encargo), importe: datosLocales.importe || '', superficie: datosLocales.superficie || '', importe_m2: datosLocales.importe_m2 || '', importe_encargo: datosLocales.importe_encargo || '' }); setModoEdicion(false); setModal(false); };

  // Sesión 34: solicitud de eliminación (comercial) o su rechazo (admin)
  const cambiarSolicitud = (solicitud) => {
    setDatosLocales(d => ({ ...d, solicitud_eliminacion: solicitud }));
    onActualizar({ id: presupuesto.id, solicitud_eliminacion: solicitud });
  };

  const eliminarPresupuesto = async () => {
    setEliminando(true);
    try {
      // Sesión 34: borrado común (operaciones.js): recalcula el estado de la oportunidad y limpia el vínculo del proyecto
      const r = await ejecutarEliminacion('presupuesto', presupuesto.id, opVinculada ? datosLocales : { ...datosLocales, oportunidadId: null });
      if (r.estadoOp && onEstadoOpCambiado) onEstadoOpCambiado(r.oportunidadId, r.estadoOp);
      onEliminar(presupuesto.id);
    } catch (e) { console.error(e); setEliminando(false); }
  };

  return (
    <div style={{ ...styles.container, paddingBottom: modoEdicion ? '100px' : '32px' }}>
      {modal && <ModalConfirmar titulo="Cambios sin guardar" mensaje="¿Quieres salir sin guardar los cambios de este presupuesto?" textoCancelar="Seguir editando" textoConfirmar="Salir sin guardar" onConfirmar={confirmarSalida} onCancelar={() => setModal(false)} />}
      {modalOp && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }} onClick={() => !vinculando && setModalOp(false)}>
          <div style={{ background: '#1a1440', borderRadius: '12px', padding: '28px', maxWidth: '560px', width: '92%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', border: '1px solid rgba(83,74,183,0.3)', boxShadow: '0 24px 48px rgba(0,0,0,0.4)' }} onClick={e => e.stopPropagation()}>
            <p style={{ color: '#ffffff', fontSize: '1rem', fontWeight: '500', marginBottom: '4px' }}>Vincular oportunidad</p>
            <p style={{ color: '#AFA9EC', fontSize: '0.85rem', marginBottom: '16px' }}>Oportunidades del lead {datosLocales.leadNombre}</p>
            <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, minHeight: 0 }}>
              {cargandoOps ? <p style={{ color: '#AFA9EC', fontSize: '0.85rem' }}>Cargando oportunidades...</p> :
                opsLead.length === 0 ? <p style={{ color: '#AFA9EC', fontSize: '0.85rem' }}>{datosLocales.leadId ? 'Este lead no tiene oportunidades registradas. Créala desde el módulo Oportunidades.' : 'Este presupuesto no tiene lead asociado.'}</p> :
                opsLead.map(op => {
                  const esActual = op.id === datosLocales.oportunidadId;
                  const vinculadosOtros = presupuestos.filter(p => p.oportunidadId === op.id && p.id !== presupuesto.id);
                  return (
                    <div key={op.id} style={{ ...styles.opcionOportunidad, cursor: esActual ? 'default' : 'pointer', opacity: esActual ? 0.55 : 1, borderColor: opElegida?.id === op.id ? '#1D9E75' : 'rgba(83,74,183,0.2)', background: opElegida?.id === op.id ? 'rgba(29,158,117,0.1)' : '#0e0b24' }} onClick={() => !esActual && setOpElegida(op)}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px', flexWrap: 'wrap' }}>
                        <span style={{ background: estadoOp(op.estado).color, color: '#fff', fontSize: '0.68rem', padding: '2px 8px', borderRadius: '20px' }}>{estadoOp(op.estado).etiqueta}</span>
                        {op.sector && <span style={{ color: '#AFA9EC', fontSize: '0.78rem' }}>{op.sector}</span>}
                        <span style={{ color: '#AFA9EC', fontSize: '0.78rem' }}>{formatearFechaCorta(op.fecha_entrada)}</span>
                        {esActual && <span style={{ color: '#1D9E75', fontSize: '0.75rem' }}>Vinculada actualmente</span>}
                      </div>
                      <p style={{ color: '#ffffff', fontSize: '0.85rem' }}>{[op.ciudad, op.provincia].filter(Boolean).join(', ') || 'Sin ubicación'}{op.superficie && <span style={{ color: '#AFA9EC', marginLeft: '8px' }}>{op.superficie} m²</span>}</p>
                      {vinculadosOtros.length > 0 && (
                        <p style={{ color: '#AFA9EC', fontSize: '0.75rem', marginTop: '6px' }}>Vinculada a {vinculadosOtros.length} presupuesto{vinculadosOtros.length > 1 ? 's' : ''}: {vinculadosOtros.map(p => `nº ${p.num_presupuesto || '—'}`).join(', ')}</p>
                      )}
                    </div>
                  );
                })}
            </div>
            {opElegida && (
              <div style={{ background: 'rgba(83,74,183,0.12)', border: '1px solid rgba(83,74,183,0.4)', borderRadius: '8px', padding: '12px 16px', marginTop: '14px' }}>
                <p style={{ color: '#AFA9EC', fontSize: '0.82rem', lineHeight: '1.5' }}>
                  La oportunidad seleccionada quedará como <span style={{ color: estadoOp(estadoDestino).color === '#6B7280' ? '#ffffff' : estadoOp(estadoDestino).color, fontWeight: '500' }}>{estadoOp(estadoDestino).etiqueta}</span>.
                  {esRelink && <> La oportunidad anterior quedará como <span style={{ color: '#ffffff', fontWeight: '500' }}>{estadoOp(estadoAnteriorTras).etiqueta}</span>.</>}
                </p>
              </div>
            )}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button style={styles.botonSecundario} onClick={() => setModalOp(false)} disabled={vinculando}>Cancelar</button>
              <button style={{ ...styles.botonPrimario, opacity: opElegida ? 1 : 0.5, cursor: opElegida ? 'pointer' : 'not-allowed' }} onClick={vincularOportunidad} disabled={!opElegida || vinculando}>{vinculando ? 'Vinculando...' : 'Vincular'}</button>
            </div>
          </div>
        </div>
      )}
      {modalEliminar && (
        <ModalConfirmar
          titulo="Eliminar presupuesto"
          mensaje={`¿Seguro que quieres eliminar el presupuesto ${datosLocales.num_presupuesto || ''}? Esta acción no se puede deshacer.`}
          textoCancelar="Cancelar"
          textoConfirmar={eliminando ? 'Eliminando...' : 'Sí, eliminar'}
          onConfirmar={eliminarPresupuesto}
          onCancelar={() => setModalEliminar(false)}
        />
      )}
      <div style={styles.cabecera}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <button style={styles.botonVolver} onClick={() => modoEdicion ? setModal(true) : onVolver()}>← Presupuestos</button>
          <h1 style={styles.titulo}>{datosLocales.num_presupuesto || 'Sin número'}</h1>
          <EstadoBadge estado={form.estado} />
        </div>
        {!modoEdicion && <div style={{ display: 'flex', gap: '10px' }}>
          {esAdmin
            ? <button style={styles.botonEliminar} onClick={() => setModalEliminar(true)}>Eliminar presupuesto</button>
            : <BotonSolicitarEliminacion coleccion="presupuestos" docId={presupuesto.id} solicitud={datosLocales.solicitud_eliminacion} nombreRegistro={datosLocales.num_presupuesto} onCambio={cambiarSolicitud} />}
          <button style={styles.botonSecundario} onClick={() => setModoEdicion(true)}>Editar</button>
          {datosLocales.estado === 'aceptado' && (
            datosLocales.proyectoId
              ? <button style={{ ...styles.botonProyecto, background: 'transparent', border: '1px solid #1D9E75', color: '#1D9E75' }} onClick={() => onNavegar && onNavegar({ modulo: 'proyectos', accion: 'ficha', id: datosLocales.proyectoId })}>Ver proyecto →</button>
              : <button style={styles.botonProyecto} onClick={() => onNavegar && onNavegar({ modulo: 'proyectos', accion: 'nuevo', presupuestoId: datosLocales.id, origen: 'presupuestos' })}>→ Crear proyecto</button>
          )}
        </div>}
      </div>

      {!modoEdicion && (
        <AvisoSolicitudEliminacion coleccion="presupuestos" docId={presupuesto.id} solicitud={datosLocales.solicitud_eliminacion} esAdmin={esAdmin}
          onCambio={cambiarSolicitud} onEliminar={() => setModalEliminar(true)} />
      )}
      {modoEdicion && avisoBorrador && (
        <div style={{ padding: '16px 32px 0' }}>
          <AvisoBorrador onDescartar={confirmarSalida} />
        </div>
      )}
      <div style={styles.contenido}>
      <div style={styles.fichaGrid}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={styles.bloque}>
            <h2 style={styles.bloqueTitulo}>Lead y oportunidad</h2>
            <div style={styles.datosGrid}>
              <DatoFila label="Lead" valor={datosLocales.leadNombre} />
              {datosLocales.leadEmpresa && <DatoFila label="Empresa" valor={datosLocales.leadEmpresa} />}
              {datosLocales.oportunidad_ciudad && <DatoFila label="Ubicación proyecto" valor={datosLocales.oportunidad_ciudad} />}
            </div>
            <div style={{ marginTop: '18px', paddingTop: '16px', borderTop: '1px solid rgba(83,74,183,0.2)' }}>
              <p style={{ ...styles.bloqueTitulo, marginBottom: '12px' }}>Oportunidad vinculada</p>
              {estadoVinculo === 'cargando' && <p style={{ color: '#AFA9EC', fontSize: '0.85rem' }}>Cargando...</p>}
              {estadoVinculo === 'ok' && (
                <div style={{ background: '#0e0b24', borderRadius: '8px', padding: '12px 16px', border: '1px solid rgba(29,158,117,0.4)' }}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px', flexWrap: 'wrap' }}>
                    <span style={{ background: estadoOp(opVinculada.estado).color, color: '#fff', fontSize: '0.68rem', padding: '2px 8px', borderRadius: '20px' }}>{estadoOp(opVinculada.estado).etiqueta}</span>
                    {opVinculada.sector && <span style={{ color: '#AFA9EC', fontSize: '0.78rem' }}>{opVinculada.sector}</span>}
                    <span style={{ color: '#AFA9EC', fontSize: '0.78rem' }}>{formatearFechaCorta(opVinculada.fecha_entrada)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                    <p style={{ color: '#ffffff', fontSize: '0.85rem' }}>{[opVinculada.ciudad, opVinculada.provincia].filter(Boolean).join(', ') || 'Sin ubicación'}{opVinculada.superficie && <span style={{ color: '#AFA9EC', marginLeft: '8px' }}>{opVinculada.superficie} m²</span>}</p>
                    {modoEdicion && <button style={styles.botonEditar} onClick={abrirSelectorOp}>Cambiar</button>}
                  </div>
                  {otrosPresupuestosOp.length > 0 && (
                    <p style={{ color: '#AFA9EC', fontSize: '0.75rem', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(83,74,183,0.15)' }}>Otros presupuestos de esta oportunidad: {otrosPresupuestosOp.map(p => `nº ${p.num_presupuesto || '—'}`).join(', ')}</p>
                  )}
                </div>
              )}
              {estadoVinculo === 'rota' && (
                <div style={{ background: 'rgba(216,90,48,0.08)', border: '1px solid rgba(216,90,48,0.4)', borderRadius: '8px', padding: '12px 16px' }}>
                  <p style={{ color: '#D85A30', fontSize: '0.85rem', marginBottom: '10px' }}>Vínculo roto: la oportunidad referenciada ya no existe.</p>
                  <button style={styles.botonPrimario} onClick={abrirSelectorOp}>Vincular oportunidad</button>
                </div>
              )}
              {estadoVinculo === 'sin' && (
                <div style={{ background: 'rgba(186,117,23,0.08)', border: '1px solid rgba(186,117,23,0.3)', borderRadius: '8px', padding: '12px 16px' }}>
                  <p style={{ color: '#BA7517', fontSize: '0.85rem', marginBottom: '10px' }}>Sin oportunidad vinculada.</p>
                  <button style={styles.botonPrimario} onClick={abrirSelectorOp}>Vincular oportunidad</button>
                </div>
              )}
            </div>
          </div>
          <div style={styles.bloque}>
            <h2 style={styles.bloqueTitulo}>Datos del presupuesto</h2>
            {modoEdicion ? (
              <div style={styles.camposGrid}>
                <Campo label="Nº Presupuesto"><input style={estiloInput()} value={form.num_presupuesto || ''} onChange={e => set('num_presupuesto', e.target.value)} /></Campo>
                <Campo label="Fecha"><input type="date" className="metron-date" style={estiloInput()} value={form.fecha_presupuesto} onChange={e => set('fecha_presupuesto', e.target.value)} /></Campo>
                <Campo label="Sector"><select style={estiloInput()} value={form.sector || ''} onChange={e => set('sector', e.target.value)}><option value="">Sin sector</option>{sectores.map(s => <option key={s} value={s}>{s}</option>)}</select></Campo>
                <Campo label="Tipo de encargo"><select style={estiloInput()} value={form.tipo_encargo || ''} onChange={e => set('tipo_encargo', e.target.value)}><option value="">Sin tipo</option>{tiposProyecto.map(t => <option key={t} value={t}>{t}</option>)}</select></Campo>
                <Campo label="Importe (€)"><input type="number" style={estiloInput()} value={form.importe} onChange={e => set('importe', e.target.value)} /></Campo>
                <Campo label="Superficie (m²)"><input type="number" style={estiloInput()} value={form.superficie} onChange={e => set('superficie', e.target.value)} /></Campo>
                <Campo label="Descripción" fullWidth><textarea style={{ ...estiloInput(), resize: 'vertical', minHeight: '80px' }} value={form.descripcion || ''} onChange={e => set('descripcion', e.target.value)} /></Campo>
              </div>
            ) : (
              <div style={styles.datosGrid}>
                <DatoFila label="Fecha" valor={formatearFechaCorta(datosLocales.fecha_presupuesto)} />
                <DatoFila label="Sector" valor={datosLocales.sector || '—'} />
                <DatoFila label="Tipo" valor={datosLocales.tipo_encargo || '—'} />
                <DatoFila label="Importe" valor={formatearEuros(datosLocales.importe)} />
                <DatoFila label="Superficie" valor={datosLocales.superficie ? `${datosLocales.superficie} m²` : '—'} />
                <DatoFila label="€/m²" valor={datosLocales.importe_m2 ? `${fmtNum(Math.round(datosLocales.importe_m2))} €/m²` : '—'} />
                {datosLocales.descripcion && <DatoFila label="Descripción" valor={datosLocales.descripcion} />}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={styles.bloque}>
            <h2 style={styles.bloqueTitulo}>Estado</h2>
            {modoEdicion ? (
              <>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
                  {ESTADOS_PRESUPUESTO.map(e => <button key={e.valor} style={{ ...styles.pill, background: form.estado === e.valor ? e.color : 'transparent', color: form.estado === e.valor ? '#fff' : '#AFA9EC', borderColor: form.estado === e.valor ? e.color : 'rgba(83,74,183,0.3)', padding: '8px 20px' }} onClick={() => set('estado', e.valor)}>{e.etiqueta}</button>)}
                </div>
                {form.estado === 'aceptado' && (
                  <div style={styles.panelEncargo}>
                    <p style={{ color: '#1D9E75', fontSize: '0.82rem', fontWeight: '500', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Datos del encargo</p>
                    <div style={styles.camposGrid}>
                      <Campo label="Nº Encargo"><input style={estiloInput()} value={form.num_encargo || ''} onChange={e => set('num_encargo', e.target.value)} /></Campo>
                      <Campo label="Fecha de encargo"><input type="date" className="metron-date" style={estiloInput()} value={form.fecha_encargo || ''} onChange={e => set('fecha_encargo', e.target.value)} /></Campo>
                      <Campo label="Importe encargo (€)"><input type="number" style={estiloInput()} value={form.importe_encargo || ''} onChange={e => set('importe_encargo', e.target.value)} /></Campo>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div style={styles.datosGrid}>
                <DatoFila label="Estado actual" valor={<EstadoBadge estado={datosLocales.estado} />} />
                {datosLocales.estado === 'aceptado' && (<>{datosLocales.num_encargo && <DatoFila label="Nº Encargo" valor={datosLocales.num_encargo} />}{datosLocales.fecha_encargo && <DatoFila label="Fecha encargo" valor={formatearFechaCorta(datosLocales.fecha_encargo)} />}{datosLocales.importe_encargo && <DatoFila label="Importe encargo" valor={formatearEuros(datosLocales.importe_encargo)} />}</>)}
              </div>
            )}
          </div>
          <div style={styles.bloque}>
            <h2 style={styles.bloqueTitulo}>Resumen económico</h2>
            <div style={styles.datosGrid}>
              <DatoFila label="Importe presupuestado" valor={formatearEuros(datosLocales.importe)} />
              {datosLocales.estado === 'aceptado' && datosLocales.importe_encargo && datosLocales.importe_encargo !== datosLocales.importe && <DatoFila label="Importe encargo" valor={formatearEuros(datosLocales.importe_encargo)} />}
              <DatoFila label="Superficie" valor={datosLocales.superficie ? `${datosLocales.superficie} m²` : '—'} />
              <DatoFila label="Precio por m²" valor={datosLocales.importe_m2 ? `${fmtNum(Math.round(datosLocales.importe_m2))} €/m²` : '—'} />
            </div>
          </div>
        </div>
      </div>
      </div>{/* fin contenido */}

      {modoEdicion && (
        <div style={styles.barraGuardar}>
          <button style={styles.botonSecundario} onClick={() => setModal(true)}>Cancelar</button>
          <button style={styles.botonPrimario} onClick={guardar} disabled={guardando}>{guardando ? 'Guardando...' : 'Guardar cambios'}</button>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: '0', maxWidth: '100%' },
  cabecera: { position: 'sticky', top: 0, zIndex: 50, background: '#0e0b24', borderBottom: '1px solid rgba(83,74,183,0.2)', padding: '20px 32px 12px', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' },
  contenido: { padding: '24px 32px 32px' },
  titulo: { color: '#ffffff', fontSize: '1.6rem', fontWeight: '300', letterSpacing: '0.02em' },
  botonPrimario: { background: '#534AB7', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 18px', fontSize: '0.88rem', fontWeight: '500', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  botonSecundario: { background: 'transparent', color: '#AFA9EC', border: '1px solid rgba(83,74,183,0.4)', borderRadius: '8px', padding: '10px 18px', fontSize: '0.88rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  botonVolver: { background: 'transparent', color: '#7B6FE8', border: 'none', fontSize: '0.88rem', cursor: 'pointer', padding: '0', fontFamily: 'DM Sans, sans-serif' },
  botonEditar: { background: 'transparent', color: '#7B6FE8', border: 'none', fontSize: '0.78rem', cursor: 'pointer', textDecoration: 'underline', fontFamily: 'DM Sans, sans-serif' },
  botonLimpiar: { background: 'transparent', color: '#D85A30', border: '1px solid #D85A30', borderRadius: '8px', padding: '8px 14px', fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  botonEliminar: { background: 'transparent', color: '#D85A30', border: '1px solid #D85A30', borderRadius: '8px', padding: '10px 18px', fontSize: '0.88rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  botonProyecto: { background: '#1D9E75', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 18px', fontSize: '0.88rem', fontWeight: '500', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  botonAceptar: { background: '#1D9E75', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 14px', fontSize: '0.82rem', fontWeight: '500', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  botonCerrar: { background: 'transparent', color: '#6B7280', border: '1px solid #6B7280', borderRadius: '8px', padding: '8px 14px', fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  barraGuardar: { position: 'fixed', bottom: 0, left: 0, right: 0, background: '#1a1440', borderTop: '1px solid rgba(83,74,183,0.3)', padding: '16px 32px', display: 'flex', justifyContent: 'flex-end', gap: '12px', zIndex: 100 },
  gridKpis: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '20px' },
  kpiCard: { background: '#1a1440', borderRadius: '10px', padding: '16px 20px', border: '1px solid rgba(83,74,183,0.2)' },
  kpiLabel: { color: '#AFA9EC', fontSize: '0.75rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' },
  kpiValor: { color: '#ffffff', fontSize: '1.6rem', fontWeight: '300' },
  filtrosBar: { display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px', alignItems: 'center' },
  buscador: { background: '#1a1440', border: '1px solid rgba(83,74,183,0.3)', borderRadius: '8px', padding: '9px 14px', color: '#fff', fontSize: '0.88rem', outline: 'none', minWidth: '240px', flex: '1', fontFamily: 'DM Sans, sans-serif' },
  pills: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  pill: { border: '1px solid', borderRadius: '20px', padding: '6px 14px', fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', background: 'transparent' },
  select: { background: '#1a1440', border: '1px solid rgba(83,74,183,0.3)', borderRadius: '8px', padding: '9px 14px', color: '#AFA9EC', fontSize: '0.88rem', outline: 'none', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  tablaWrapper: { overflowX: 'auto' },
  tabla: { width: '100%', borderCollapse: 'collapse' },
  th: { color: '#AFA9EC', fontSize: '0.75rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '10px 14px', textAlign: 'left', borderBottom: '1px solid rgba(83,74,183,0.2)' },
  tr: { borderBottom: '1px solid rgba(83,74,183,0.1)', cursor: 'pointer' },
  td: { padding: '12px 14px', color: '#ffffff', fontSize: '0.88rem', verticalAlign: 'middle' },
  nombreLead: { display: 'block', fontWeight: '500' },
  subLead: { display: 'block', color: '#AFA9EC', fontSize: '0.78rem' },
  formGrid: { display: 'flex', flexDirection: 'column', gap: '20px' },
  seccion: { background: '#1a1440', borderRadius: '12px', padding: '24px', border: '1px solid rgba(83,74,183,0.2)' },
  seccionTitulo: { color: '#ffffff', fontSize: '0.9rem', fontWeight: '500', marginBottom: '20px', paddingBottom: '10px', borderBottom: '1px solid rgba(83,74,183,0.2)' },
  camposGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' },
  label: { color: '#AFA9EC', fontSize: '0.78rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.04em' },
  fichaGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' },
  bloque: { background: '#1a1440', borderRadius: '12px', padding: '24px', border: '1px solid rgba(83,74,183,0.2)' },
  bloqueTitulo: { color: '#AFA9EC', fontSize: '0.78rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '16px' },
  datosGrid: { display: 'flex', flexDirection: 'column', gap: '10px' },
  datoFila: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' },
  datoLabel: { color: '#AFA9EC', fontSize: '0.82rem', flexShrink: 0 },
  datoValor: { color: '#ffffff', fontSize: '0.88rem', textAlign: 'right' },
  seleccionado: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#0e0b24', borderRadius: '8px', padding: '12px 16px', border: '1px solid #1D9E75' },
  desplegable: { background: '#0e0b24', border: '1px solid rgba(83,74,183,0.3)', borderRadius: '8px', marginTop: '4px', overflow: 'hidden' },
  opcionDesplegable: { padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid rgba(83,74,183,0.1)' },
  opcionOportunidad: { background: '#0e0b24', borderRadius: '8px', padding: '12px 16px', border: '1px solid', cursor: 'pointer', transition: 'border-color 0.15s' },
  panelEncargo: { background: 'rgba(29,158,117,0.08)', borderRadius: '8px', padding: '16px', border: '1px solid rgba(29,158,117,0.3)', marginTop: '16px' },
  error: { color: '#D85A30', fontSize: '0.78rem', marginTop: '6px' },
};
